import React, { useState, useEffect } from 'react';
import { Menu, X, ArrowUpRight, Zap, Eye } from 'lucide-react';
import { SUMMIT_CONFIG } from '../config/summitConfig';

interface NavbarProps {
  reducedMotion: boolean;
  onToggleReducedMotion: () => void;
}

export const Navbar: React.FC<NavbarProps> = ({
  reducedMotion,
  onToggleReducedMotion,
}) => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [activeSection, setActiveSection] = useState('home');

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 40);

      // Simple active section detection
      const sections = ['home', 'about', 'committees', 'matrices', 'advisors', 'registration', 'contact'];
      for (const section of sections) {
        const el = document.getElementById(section);
        if (el) {
          const rect = el.getBoundingClientRect();
          if (rect.top <= 200 && rect.bottom >= 200) {
            setActiveSection(section);
            break;
          }
        }
      }
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollToSection = (id: string) => {
    setMobileMenuOpen(false);
    const element = document.getElementById(id);
    if (element) {
      element.scrollIntoView({ behavior: reducedMotion ? 'auto' : 'smooth' });
    }
  };

  const navLinks = [
    { id: 'home', label: 'HOME' },
    { id: 'about', label: 'ABOUT' },
    { id: 'committees', label: 'COMMITTEES' },
    { id: 'matrices', label: 'MATRICES' },
    { id: 'advisors', label: 'ADVISORS' },
    { id: 'registration', label: 'REGISTRATION' },
    { id: 'contact', label: 'CONTACT' },
  ];

  return (
    <>
      <header 
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
          isScrolled 
            ? 'bg-deepblack/85 backdrop-blur-md border-b border-bronze/30 py-3 shadow-lg shadow-black/40' 
            : 'bg-transparent py-5'
        }`}
      >
        <div className="w-full px-6 sm:px-10 lg:px-16 xl:px-24 flex items-center justify-between">
          
          {/* FLSxMUN 2.0 Monogram & Logo */}
          <button 
            onClick={() => scrollToSection('home')}
            className="flex items-center gap-3 text-left group focus:outline-none"
            data-cursor-interactive
            data-cursor-text="FLSxMUN"
          >
            {/* Architectural Official Logo Crest */}
            <div className="w-10 h-10 border border-bronze/60 bg-gradient-to-br from-charcoal-subtle to-deepblack flex items-center justify-center rounded-sm transition-transform duration-300 group-hover:scale-105 group-hover:border-beige p-1">
              <img src="/logo.png" alt="FLSxMUN 2.0 Logo" className="w-full h-full object-contain" />
            </div>
            <div className="flex flex-col">
              <span className="font-serif font-bold text-lg tracking-widest text-beige leading-tight group-hover:text-ivory transition-colors">
                {SUMMIT_CONFIG.shortName}
              </span>
              <span className="text-[9px] uppercase tracking-widest-plus text-beige/60 font-sans">
                {SUMMIT_CONFIG.dates}
              </span>
            </div>
          </button>

          {/* Desktop Navigation Links */}
          <nav className="hidden md:flex items-center gap-7 lg:gap-9">
            {navLinks.map((link) => (
              <button
                key={link.id}
                onClick={() => scrollToSection(link.id)}
                className={`text-xs uppercase tracking-widest font-medium transition-all relative py-1 focus:outline-none ${
                  activeSection === link.id
                    ? 'text-beige font-semibold'
                    : 'text-ivory/65 hover:text-beige'
                }`}
                data-cursor-interactive
              >
                {link.label}
                {activeSection === link.id && (
                  <span className="absolute bottom-0 left-0 right-0 h-[1.5px] bg-gradient-to-r from-bronze via-beige to-bronze" />
                )}
              </button>
            ))}
          </nav>

          {/* Right Action Items */}
          <div className="hidden md:flex items-center gap-4">
            {/* Reduced Motion Toggle Button */}
            <button
              onClick={onToggleReducedMotion}
              title={reducedMotion ? "Enable animations" : "Reduce motion for accessibility"}
              className={`p-2 rounded border transition-colors ${
                reducedMotion
                  ? 'border-beige text-beige bg-beige/10'
                  : 'border-white/10 text-ivory/60 hover:text-beige hover:border-bronze/40'
              }`}
            >
              <Eye className="w-4 h-4" />
            </button>

            {/* Primary CTA */}
            <button
              onClick={() => scrollToSection('registration')}
              className="relative px-5 py-2.5 bg-gradient-to-r from-beige-300 via-beige to-beige-400 text-deepblack text-xs font-semibold uppercase tracking-widest rounded-sm border border-beige hover:shadow-glow-bronze transition-all duration-200 transform hover:-translate-y-0.5 active:translate-y-0 flex items-center gap-1.5"
              data-cursor-interactive
              data-cursor-text="JOIN"
            >
              <span>REGISTER NOW</span>
              <ArrowUpRight className="w-3.5 h-3.5" />
            </button>
          </div>

          {/* Mobile Hamburger Button */}
          <div className="flex md:hidden items-center gap-2">
            <button
              onClick={onToggleReducedMotion}
              title="Toggle motion"
              className="p-2 border border-white/10 text-ivory/70 rounded"
            >
              <Eye className="w-4 h-4" />
            </button>

            <button
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              className="p-2 rounded border border-bronze/40 text-beige hover:bg-beige/10 transition-colors focus:outline-none"
              aria-label="Toggle mobile menu"
            >
              {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </button>
          </div>

        </div>
      </header>

      {/* Fullscreen Mobile Navigation Drawer */}
      <div 
        className={`fixed inset-0 z-40 bg-deepblack/98 backdrop-blur-xl md:hidden flex flex-col justify-between p-8 pt-28 transition-all duration-500 ease-in-out ${
          mobileMenuOpen ? 'opacity-100 pointer-events-auto translate-x-0' : 'opacity-0 pointer-events-none translate-x-full'
        }`}
      >
        {/* Greek Key Top Border in Mobile Drawer */}
        <div className="w-full h-3 greek-key-border opacity-50 mb-6" />

        <div className="flex flex-col gap-6">
          <span className="text-[10px] tracking-architectural text-bronze uppercase">
            CONFERENCE NAVIGATION
          </span>
          {navLinks.map((link, idx) => (
            <button
              key={link.id}
              onClick={() => scrollToSection(link.id)}
              className="text-left font-serif text-2xl tracking-widest text-ivory/85 hover:text-beige flex items-center justify-between border-b border-white/5 pb-3 transition-colors"
              style={{ transitionDelay: `${idx * 50}ms` }}
            >
              <span>{link.label}</span>
              <span className="text-xs font-mono text-bronze">0{idx + 1}</span>
            </button>
          ))}
        </div>

        <div className="flex flex-col gap-4 mt-8 pt-6 border-t border-bronze/30">
          <button
            onClick={() => scrollToSection('registration')}
            className="w-full py-4 bg-beige text-deepblack font-serif font-bold text-center tracking-widest text-sm uppercase rounded shadow-lg"
          >
            REGISTER NOW →
          </button>
          
          <div className="flex justify-between items-center text-xs text-ivory/50">
            <span>FLSxMUN 2.0 • {SUMMIT_CONFIG.dates}</span>
            <span className="text-bronze font-mono">DIPLOMATIC SUMMIT</span>
          </div>
        </div>
      </div>
    </>
  );
};
